import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Psychology as AIIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

const PerformanceReviewList = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [page, rowsPerPage]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-performance/review/list', {
        page: page + 1,
        limit: rowsPerPage,
        searchTerm: searchTerm
      });

      if (response.data.success) {
        setReviews(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      Swal.fire('Error', 'Failed to fetch performance reviews', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchReviews();
  };

  const handleViewDetails = (review) => {
    setSelectedReview(review);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedReview(null);
  };

  const getRatingColor = (rating) => {
    const colors = {
      OUTSTANDING: 'success',
      EXCEEDS_EXPECTATIONS: 'info',
      MEETS_EXPECTATIONS: 'primary',
      NEEDS_IMPROVEMENT: 'warning',
      UNSATISFACTORY: 'error'
    };
    return colors[rating] || 'default';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'IMPROVING') return <TrendingUpIcon color="success" />;
    if (trend === 'DECLINING') return <TrendingDownIcon color="error" />;
    return <TrendingFlatIcon color="action" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold">
          Performance Reviews
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/userdashboard/staff/hrm/performance/review/create')}
            color="primary"
          >
            Create Review
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchReviews}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by employee name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Employee</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Review Period</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Review Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rating</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Score</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Achievement</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Trend</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">No reviews found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((review) => (
                <TableRow key={review._id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {review.employeeId?.staff_name || review.employeeId?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {review.employeeId?.employeeCode || review.employeeId?.emp_id || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(review.reviewStartDate)} - {formatDate(review.reviewEndDate)}
                    </Typography>
                    <Chip label={review.reviewPeriod} size="small" sx={{ mt: 0.5 }} />
                  </TableCell>
                  <TableCell>{formatDate(review.reviewDate)}</TableCell>
                  <TableCell>
                    <Chip
                      label={review.rating?.replace(/_/g, ' ')}
                      color={getRatingColor(review.rating)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {review.totalScore} / {review.maxScore}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {review.achievementPercentage}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={review.aiInsights?.performanceTrend || 'N/A'}>
                      {getTrendIcon(review.aiInsights?.performanceTrend)}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleViewDetails(review)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedReview && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Performance Review Details</Typography>
                <Chip
                  label={selectedReview.rating?.replace(/_/g, ' ')}
                  color={getRatingColor(selectedReview.rating)}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Employee Info */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Employee Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Name:</strong> {selectedReview.employeeId?.staff_name || selectedReview.employeeId?.name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Employee Code:</strong> {selectedReview.employeeId?.employeeCode || selectedReview.employeeId?.emp_id || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Department:</strong> {selectedReview.employeeId?.department?.name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Designation:</strong> {selectedReview.employeeId?.designation?.name || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Review Period & Scores */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Review Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Review Period:</strong> {selectedReview.reviewPeriod}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Review Date:</strong> {formatDate(selectedReview.reviewDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Period:</strong> {formatDate(selectedReview.reviewStartDate)} - {formatDate(selectedReview.reviewEndDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Reviewed By:</strong> {selectedReview.reviewedBy?.name || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="h4" color="primary">{selectedReview.totalScore}</Typography>
                      <Typography variant="caption">Total Score</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="h4">{selectedReview.maxScore}</Typography>
                      <Typography variant="caption">Max Score</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="h4" color="success.main">{selectedReview.achievementPercentage}%</Typography>
                      <Typography variant="caption">Achievement</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="h6">{selectedReview.rating?.replace(/_/g, ' ')}</Typography>
                      <Typography variant="caption">Rating</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* AI Insights */}
              {selectedReview.aiInsights && (
                <Card sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AIIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2" color="textSecondary">
                        AI-Powered Insights
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Performance Trend:</strong> {selectedReview.aiInsights.performanceTrend}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Risk Level:</strong> {selectedReview.aiInsights.riskLevel}
                        </Typography>
                      </Grid>
                    </Grid>
                    {selectedReview.aiInsights.recommendations?.length > 0 && (
                      <>
                        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                          <strong>Recommendations:</strong>
                        </Typography>
                        <List dense>
                          {selectedReview.aiInsights.recommendations.map((rec, idx) => (
                            <ListItem key={idx}>
                              <ListItemText primary={`• ${rec}`} />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* KPI Evaluations */}
              {selectedReview.kpiEvaluations?.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      KPI Evaluations
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>KPI</TableCell>
                          <TableCell>Target</TableCell>
                          <TableCell>Achieved</TableCell>
                          <TableCell>Achievement %</TableCell>
                          <TableCell>Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedReview.kpiEvaluations.map((kpi, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{kpi.kpiId?.name || 'N/A'}</TableCell>
                            <TableCell>{kpi.targetValue}</TableCell>
                            <TableCell>{kpi.achievedValue}</TableCell>
                            <TableCell>{kpi.achievementPercentage}%</TableCell>
                            <TableCell><strong>{kpi.score}</strong></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Strengths */}
              {selectedReview.strengths?.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Strengths
                    </Typography>
                    <List dense>
                      {selectedReview.strengths.map((strength, idx) => (
                        <ListItem key={idx}>
                          <ListItemText primary={`✓ ${strength}`} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Areas of Improvement */}
              {selectedReview.areasOfImprovement?.length > 0 && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Areas of Improvement
                    </Typography>
                    <List dense>
                      {selectedReview.areasOfImprovement.map((area, idx) => (
                        <ListItem key={idx}>
                          <ListItemText primary={`• ${area}`} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Reviewer Comments */}
              {selectedReview.reviewerComments && (
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Reviewer Comments
                    </Typography>
                    <Typography variant="body2">{selectedReview.reviewerComments}</Typography>
                  </CardContent>
                </Card>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PerformanceReviewList;
